import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FixedLicenseFrame, {FRAME, fixedCrop} from "./FixedLicenseFrame";
import { licensePrintLayout } from "@/lib/licensePrintLayout";
import { api } from "@/lib/api";
import "./mt-license-builder.css";

export function photoExpiration(value) {
  if (!value) return "Date needed";
  const [year,month,day]=value.split("-").map(Number);
  const last=new Date(Date.UTC(year+2,month,0)).getUTCDate();
  return `${year+2}-${String(month).padStart(2,"0")}-${String(Math.min(day,last)).padStart(2,"0")}`;
}
export const FULL_CROP = { x: 0, y: 0, width: 1, height: 1 };
export const floridaToday = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
export function squareCrop(width, height) {
  const w = Math.min(1, height / width),
    h = (w * width) / height;
  return { x: (1 - w) / 2, y: (1 - h) / 2, width: w, height: h };
}
export function fitCrop(crop, field, value, ratio = null) {
  const next = { ...crop, [field]: value };
  if (ratio && field === "width") next.height = value * ratio;
  next.width = Math.min(next.width, 1);
  next.height = Math.min(next.height, 1);
  next.x = Math.max(0, Math.min(next.x, 1 - next.width));
  next.y = Math.max(0, Math.min(next.y, 1 - next.height));
  return next;
}
export function localFlags(draft, hash, today = floridaToday()) {
  const flags = [];
  if (!draft.expires_on) flags.push("License expiration date needs review");
  else if (draft.expires_on < today) flags.push("License expired");
  else if ((Date.parse(draft.expires_on) - Date.parse(today)) / 86400000 <= 60)
    flags.push("License expires within 60 days");
  if (!draft.photo_taken_on) flags.push("Photo taken date needs review");
  else if (draft.photo_taken_on > today)
    flags.push("Photo taken date is in the future");
  else {
    const [y, m, d] = draft.photo_taken_on.split("-").map(Number);
    const renewal = new Date(Date.UTC(y + 2, m - 1, d));
    if (renewal.getUTCMonth() !== m - 1) renewal.setUTCDate(0);
    if (renewal.toISOString().slice(0, 10) <= today)
      flags.push("Photo update needed");
  }
  if (!hash) flags.push("Employee profile photo is missing");
  else if (draft.photo_sha256 !== hash)
    flags.push("Profile photo changed — review crop and taken date");
  return flags;
}
const initial = {
  revision: 0,
  document_id: "",
  page: 0,
  license_crop: FULL_CROP,
  photo_crop: FULL_CROP,
  photo_sha256: "",
  full_legal_name: "",
  expires_on: null,
  photo_taken_on: null,
  complete_license_confirmed: false,
  full_name_confirmed: false,
};
const problem = async (error, fallback) => {
  let value = error.response?.data;
  if (value instanceof Blob) {
    try {
      value = JSON.parse(await value.text());
    } catch {
      value = null;
    }
  }
  return typeof value?.detail === "string" ? value.detail : fallback;
};
function download(blob, name) {
  const url = URL.createObjectURL(blob),
    a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function CropEditor({ title, source, crop, onChange, square = false }) {
  const [size, setSize] = useState(null),
    [preview, setPreview] = useState("");
  useEffect(() => {
    if (!source) {
      setSize(null);
      setPreview("");
      return;
    }
    let active = true;
    const image = new Image();
    image.onload = () => {
      if (!active) return;
      setSize({ w: image.naturalWidth, h: image.naturalHeight });
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(crop.width * image.naturalWidth));
      canvas.height = Math.max(
        1,
        Math.round(crop.height * image.naturalHeight),
      );
      canvas
        .getContext("2d")
        .drawImage(
          image,
          crop.x * image.naturalWidth,
          crop.y * image.naturalHeight,
          canvas.width,
          canvas.height,
          0,
          0,
          canvas.width,
          canvas.height,
        );
      setPreview(canvas.toDataURL("image/png"));
    };
    image.src = source;
    return () => {
      active = false;
    };
  }, [source, crop]);
  const ratio = size ? size.w / size.h : 1;
  return (
    <section className="mt-license__tile mt-license__editor">
      <h3>{title}</h3>
      {source ? (
        <div className="mt-license__editor-body">
          <div
            className={`mt-license__crop${square ? " mt-license__crop--photo" : ""}`}
          >
            {preview && <img src={preview} alt={`${title} selected portion`} />}
          </div>
          <div className="mt-license__editor-controls">
            <div className="mt-license__ranges">
              {[
                ["x", "Horizontal", 1 - crop.width],
                ["y", "Vertical", 1 - crop.height],
                [
                  "width",
                  square ? "Size" : "Width",
                  square ? Math.min(1, 1 / ratio) : 1,
                ],
                ...(!square ? [["height", "Height", 1]] : []),
              ].map(([key, label, max]) => (
                <label key={key}>
                  {label}
                  <input
                    aria-label={`${title} ${label.toLowerCase()}`}
                    type="range"
                    min={["width", "height"].includes(key) ? 0.08 : 0}
                    max={Math.max(0, max)}
                    step="0.001"
                    value={crop[key]}
                    onChange={(e) =>
                      onChange(
                        fitCrop(
                          crop,
                          key,
                          Number(e.target.value),
                          square ? ratio : null,
                        ),
                      )
                    }
                  />
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={() =>
                onChange(
                  square && size ? squareCrop(size.w, size.h) : FULL_CROP,
                )
              }
            >
              Reset crop
            </button>
          </div>
        </div>
      ) : (
        <p>
          {square
            ? "An employee profile photo is needed. Update the profile photo, then reload this builder."
            : "Select a private license original first."}
        </p>
      )}
    </section>
  );
}
export default function MTLicenseBuilder({
  employee,
  file = {},
  navigationGuardRef,
}) {
  const id = encodeURIComponent(employee.user_id),
    base = `/mt-license/${id}`;
  const fileInput = useRef(null);
  const editVersion = useRef(0),
    dialogRef = useRef(null),
    triggerRef = useRef(null);
  const [dirty, setDirty] = useState(false),
    [pendingAction, setPendingAction] = useState(null);
  const [record, setRecord] = useState(null),
    [draft, setDraft] = useState(initial),
    [editingPhoto, setEditingPhoto] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [pageImage, setPageImage] = useState(null),
    [geometry, setGeometry] = useState(null),
    [photo, setPhoto] = useState(null),
    [pages, setPages] = useState(1),
    [photoError, setPhotoError] = useState("");
  const change = (patch) => {
    editVersion.current += 1;
    setDirty(true);
    setDraft((d) => ({ ...d, ...patch }));
    setNotice("Unsaved changes");
  };
  const load = () => {
    setDirty(false);
    setError("");
    setRecord(null);
    api
      .get(base)
      .then(({ data }) => {
        setRecord(data);
        setDraft({
          ...initial,
          ...data.draft,
          revision: data.revision,
          document_id: data.draft?.document_id || data.originals[0]?.id || "",
          full_legal_name:
            data.draft?.full_legal_name ||
            [file.legal_first, file.legal_last].filter(Boolean).join(" ") || employee.name || "",
        });
      })
      .catch(async (e) =>
        setError(
          await problem(
            e,
            "License records could not be loaded within your existing access.",
          ),
        ),
      );
  };
  useEffect(() => {
    load(); /* employee switch remounts the builder */
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!navigationGuardRef) return;
    const guard = (action) => {
      if (busy) return false;
      if (!dirty) return true;
      triggerRef.current = document.activeElement;
      setPendingAction(() => action);
      return false;
    };
    navigationGuardRef.current = guard;
    return () => {
      if (navigationGuardRef.current === guard)
        navigationGuardRef.current = null;
    };
  }, [navigationGuardRef, dirty, busy]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  useEffect(() => {
    if (pendingAction) dialogRef.current?.querySelector("button")?.focus();
  }, [pendingAction]);
  const stay = () => {
    setPendingAction(null);
    triggerRef.current?.focus();
  };
  useEffect(() => {
    if (!record) return;
    let active = true,
      url;
    setPhoto(null);
    setPhotoError("");
    api
      .get(`${base}/photo`, { responseType: "blob" })
      .then(({ data }) => {
        if (!active) return;
        url = URL.createObjectURL(data);
        const image = new Image();
        image.onload = () => {
          if (active) {
            setPhoto(url);
            if (
              !draft.photo_sha256 ||
              draft.photo_sha256 !== record.photo_sha256
            )
              setDraft((d) => ({
                ...d,
                photo_crop: squareCrop(image.naturalWidth, image.naturalHeight),
                photo_sha256: record.photo_sha256,
                photo_taken_on: null,
              }));
          }
        };
        image.onerror = () =>
          setPhotoError(
            "Profile photo cannot be displayed. Update the employee profile photo.",
          );
        image.src = url;
      })
      .catch(async (e) => {
        if (active)
          setPhotoError(
            await problem(
              e,
              "Profile photo is unavailable. Update the employee profile photo, then reload.",
            ),
          );
      });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [base, record]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPageImage(null);
    if (!draft.document_id) return;
    let active = true,
      url;
    setGeometry(null);
    Promise.all([
      api.get(`${base}/page/${draft.document_id}`, {
        params: { page: draft.page },
        responseType: "blob",
      }),
      api.get(`${base}/page-info/${draft.document_id}`, {
        params: { page: draft.page },
      }),
    ])
      .then(([{ data }, { data: info }]) => {
        if (!active) return;
        if (!info.source_width || !info.source_height)
          throw new Error("Missing source dimensions");
        url = URL.createObjectURL(data);
        setPageImage(url);
        setPages(info.pages);
        setGeometry(info);
        setDraft(current => {
          const c = current.license_crop;
          const ratio = info.source_width*c.width/(info.source_height*c.height);
          if (Math.abs(ratio-FRAME.width/FRAME.height)<0.002) return current;
          return {...current,license_crop:fixedCrop(info,Math.max(1,FRAME.width/info.source_width,FRAME.height/info.source_height),FRAME.sourceLeft,FRAME.sourceTop),complete_license_confirmed:false};
        });
      })
      .catch(async (e) => {
        if (active)
          setError(await problem(e, "License page could not be rendered."));
      });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [base, draft.document_id, draft.page]);
  const upload = async (event) => {
    const upload = event.target.files?.[0];
    event.target.value = "";
    if (!upload) return;
    if (
      !["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(
        upload.type,
      ) ||
      upload.size > 15 * 1024 * 1024
    ) {
      setError("Choose a PDF, PNG, JPEG or WEBP license of 15 MB or less.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", upload);
      const { data } = await api.post(`${base}/original`, body);
      setRecord((r) => ({ ...r, originals: [data.original, ...r.originals] }));
      change({
        document_id: data.original.id,
        page: 0,
        license_crop: FULL_CROP,
        complete_license_confirmed: false,
      });
      setNotice(
        "Original securely preserved. Review the complete license panel.",
      );
    } catch (e) {
      setError(await problem(e, "The original was not uploaded. Try again."));
    } finally {
      setBusy(false);
    }
  };
  const act = async (kind) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (kind === "original") {
        const { data } = await api.get(
          `${base}/original/${draft.document_id}`,
          { responseType: "blob" },
        );
        download(
          data,
          record.originals.find((x) => x.id === draft.document_id)?.filename ||
            "license-original",
        );
      } else if (kind === "save") {
        const submittedVersion = editVersion.current;
        const { data } = await api.put(base, draft);
        setDraft((d) => ({ ...d, revision: data.revision }));
        if (editVersion.current === submittedVersion) {
          setDirty(false);
          setNotice("Display settings saved.");
        } else
          setNotice("Submitted settings saved. Newer edits remain unsaved.");
      } else {
        const { data } = await api.post(`${base}/print`, draft, {
          responseType: "blob",
        });
        download(data, "mt-license-display.pdf");
        setNotice(
          "Print PDF downloaded. Choose Actual size / 100% to keep the photo 2 × 2 inches.",
        );
      }
    } catch (e) {
      setError(
        await problem(
          e,
          "The action could not be completed. Your original remains intact.",
        ),
      );
    } finally {
      setBusy(false);
    }
  };
  if (!record)
    return (
      <div className="mt-license" role={error ? "alert" : "status"}>
        {error || "Loading private license records…"}
        {error && <button onClick={load}>Try again</button>}
      </div>
    );
  const layout = licensePrintLayout(geometry, draft.license_crop);
  const flags = localFlags(draft, record.photo_sha256),
    blocking = flags.some((f) => f !== "License expires within 60 days");
  return (
    <div className="mt-license simple-license--minimal simple-license--canvas" aria-label="MT license builder">
      <input ref={fileInput} hidden aria-label="Upload MT license original" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" disabled={busy} onChange={upload}/>
      {draft.document_id && <header className="simple-license__header"><details className="simple-license__options"><summary>Options</summary><div>
        <button disabled={busy} onClick={()=>fileInput.current?.click()}>Replace license</button>
        <button disabled={busy||!photo} onClick={()=>setEditingPhoto(v=>!v)}>Adjust photo</button>
        <button disabled={busy} onClick={()=>act("save")}>Save draft</button>
        <button disabled={busy} onClick={()=>act("original")}>Download original</button>
        {record.originals.length>1&&<label>Saved originals<select value={draft.document_id} onChange={e=>change({document_id:e.target.value,page:0,license_crop:FULL_CROP,complete_license_confirmed:false})}>{record.originals.map(d=><option key={d.id} value={d.id}>{d.filename}</option>)}</select></label>}
      </div></details></header>}
      <div className="simple-license__workspace">
        <div className="simple-license__canvas">
          {!draft.document_id?<div className="simple-license__placeholder"><button className="simple-license__upload-trigger" disabled={busy} onClick={()=>fileInput.current?.click()}>Upload license</button><small>6.55 × 5 in</small></div>:<FixedLicenseFrame source={pageImage} geometry={geometry} crop={draft.license_crop} photo={photo} photoCrop={draft.photo_crop} onChange={value=>change({license_crop:value,complete_license_confirmed:false})}/>}
        </div>
        <aside className="simple-license__metadata" aria-label="License details">
          <label>Employee name<input aria-label="Employee name" maxLength={160} value={draft.full_legal_name} disabled={busy} onChange={e=>change({full_legal_name:e.target.value,full_name_confirmed:false})}/></label>
          <label>License expiration<input aria-label="License expiration" type="date" value={draft.expires_on||""} disabled={busy} onChange={e=>change({expires_on:e.target.value||null})}/></label>
          <label>Photo taken on<input aria-label="Photo taken on" type="date" max={floridaToday()} value={draft.photo_taken_on||""} disabled={busy} onChange={e=>change({photo_taken_on:e.target.value||null})}/></label>
          <label>Photo expiration<output>{photoExpiration(draft.photo_taken_on)}</output></label>
          {draft.document_id&&<><label className="simple-license__name-check"><input type="checkbox" checked={draft.full_name_confirmed} disabled={busy} onChange={e=>change({full_name_confirmed:e.target.checked})}/>Name matches license</label><label className="simple-license__name-check"><input type="checkbox" checked={draft.complete_license_confirmed} disabled={busy} onChange={e=>change({complete_license_confirmed:e.target.checked})}/>All license information remains visible</label></>}
        </aside>
      </div>
      {editingPhoto&&<section aria-label="Adjust display photo" className="mt-license__photo-adjust"><CropEditor title="Display photo" square source={photo} crop={draft.photo_crop} onChange={value=>change({photo_crop:value})}/><button onClick={()=>setEditingPhoto(false)}>Done</button></section>}
      {draft.document_id&&<footer><div className="simple-license__result-status"><span role="status">{busy?"Working securely…":notice}</span>{(photoError||flags.length>0)&&<details><summary>Needs review</summary>{photoError&&<p>{photoError}</p>}<ul>{flags.map(f=><li key={f}>{f}</li>)}</ul></details>}</div><div className="simple-license__footer-actions">
        {pages>1&&<label>Page<select aria-label="License page" value={draft.page} disabled={busy} onChange={e=>change({page:Number(e.target.value),license_crop:FULL_CROP,complete_license_confirmed:false})}>{Array.from({length:pages},(_,i)=><option key={i} value={i}>{i+1} of {pages}</option>)}</select></label>}
        <button disabled={busy||dirty||blocking||!draft.full_name_confirmed||!draft.complete_license_confirmed||!photo||!pageImage||!draft.full_legal_name.trim()||layout?.error} onClick={()=>act("print")}>Download PDF</button>
        <button className="mt-license__primary" disabled={busy||blocking||!draft.full_name_confirmed||!draft.complete_license_confirmed||!photo||!pageImage||!draft.full_legal_name.trim()||layout?.error} onClick={()=>act("save")}>Approve result</button>
      </div></footer>}
      {error&&<p className="mt-license__error" role="alert">{error}</p>}
      {pendingAction &&
        createPortal(
          <div className="mt-license__discard-backdrop">
            <section
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="mt-discard-title"
              className="mt-license__discard-dialog"
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  stay();
                }
                if (event.key === "Tab") {
                  event.preventDefault();
                  const buttons = [
                      ...event.currentTarget.querySelectorAll("button"),
                    ],
                    index = buttons.indexOf(document.activeElement);
                  buttons[
                    (index + (event.shiftKey ? buttons.length - 1 : 1)) %
                      buttons.length
                  ]?.focus();
                }
              }}
            >
              <h2 id="mt-discard-title">
                Discard unsaved license display changes?
              </h2>
              <p>
                Your saved settings and private originals will remain unchanged.
              </p>
              <div>
                <button type="button" onClick={stay}>
                  Stay and keep editing
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const action = pendingAction;
                    setPendingAction(null);
                    setDirty(false);
                    action();
                  }}
                >
                  Discard changes and leave
                </button>
              </div>
            </section>
          </div>,
          document.body,
        )}
    </div>
  );
}
